<template>
  <overview-section-card>
    <v-card-title>
        <span class="overview-section-title">{{ $t('overview.project_stats') }}</span>
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedDays"
          :items="daysOptions"
          item-title="title"
          item-value="value"
          :label="$t('overview.time_period')"
          density="compact"
          variant="outlined"
          style="max-width: 150px;"
          @update:model-value="loadStats"
        ></v-select>
      </v-card-title>
      <v-card-text class="pa-4">
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else>
          <div v-if="chartRows.length === 0" class="text-center pa-8">
            <p class="text--secondary">{{ $t('overview.no_stats_available') }}</p>
          </div>
          <div v-else>
            <div class="chart-wrapper">
              <div class="y-axis-labels">
                <div
                  v-for="(label, index) in yAxisLabels"
                  :key="'y-label-' + index"
                  class="y-axis-label"
                >
                  {{ label }}
                </div>
              </div>
              <div class="sparkline-container">
                <svg
                  :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
                  class="chart-svg"
                  preserveAspectRatio="none"
                >
                  <polyline
                    v-for="series in visibleProjectSeries"
                    :key="series.key"
                    :points="series.path"
                    fill="none"
                    :stroke="series.color"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <polyline
                    v-for="series in visibleUserSeries"
                    :key="'user-' + series.id"
                    :points="series.path"
                    fill="none"
                    :stroke="series.color"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="mt-6 d-flex justify-center flex-wrap">
            <v-chip
              v-for="series in projectSeries"
              :key="'chip-' + series.key"
              :id="'overview-stats-series-chip-' + series.key"
              small
              :color="series.color"
              text-color="white"
              :class="['mr-2', 'mb-2', { 'overview-stats-series-chip-hidden': !series.visible }]"
            >
              <v-icon
                :id="'overview-stats-series-chip-eye-' + series.key"
                size="x-small"
                class="overview-stats-user-chip-eye mr-1"
                :data-visible="series.visible ? 'true' : 'false'"
                :aria-label="series.visible ? $t('overview.hide_project_series') : $t('overview.show_project_series')"
                @click.stop="toggleSeriesVisibility(series.key)"
              >
                {{ series.visible ? 'mdi-eye' : 'mdi-eye-off' }}
              </v-icon>
              {{ $t('overview.' + series.key) }}
            </v-chip>
            <v-chip
              v-for="series in userSeries"
              :key="'chip-' + series.id"
              :id="'overview-stats-user-chip-' + series.id"
              small
              closable
              :color="series.color"
              text-color="white"
              :class="['mr-2', 'mb-2', { 'overview-stats-user-chip-hidden': !series.visible }]"
              @click:close="removeUser(series.id)"
            >
              <v-icon
                :id="'overview-stats-user-chip-eye-' + series.id"
                size="x-small"
                class="overview-stats-user-chip-eye mr-1"
                :data-visible="series.visible ? 'true' : 'false'"
                :aria-label="series.visible ? $t('overview.hide_user_series') : $t('overview.show_user_series')"
                @click.stop="toggleUserVisibility(series.id)"
              >
                {{ series.visible ? 'mdi-eye' : 'mdi-eye-off' }}
              </v-icon>
              {{ $t('overview.saved_assets_by_user', { user: series.label }) }}
            </v-chip>
          </div>

          <div class="mt-4 overview-stats-add-user">
            <autocomplete
              id="overview-stats-add-user"
              :options="availableUserOptions"
              :placeholder="$t('overview.add_user_to_graph')"
              :field_label="$t('overview.add_user_to_graph')"
              :clear_on_select="true"
              label="name"
              @select="onAddUser"
            ></autocomplete>
          </div>
        </div>
      </v-card-text>
  </overview-section-card>
</template>

<script>
import OverviewSectionCard from '@/components/overview/OverviewSectionCard.vue';
import Autocomplete from '@/components/Autocomplete.vue';
import LoadUsersMixin from '@/mixins/LoadUsersMixin.vue';
import { editorDisplayName } from '@/utils/assetEditors';
import {
  addOverviewGraphUserId,
  getOverviewGraphHiddenSeriesKeys,
  getOverviewGraphHiddenUserIds,
  getOverviewGraphUserIds,
  removeOverviewGraphUserId,
  toggleOverviewGraphSeriesVisibility,
  toggleOverviewGraphUserVisibility,
} from '@/utils/overviewGraphUsers';

const PROJECT_SERIES = [
  { key: 'items_added', color: '#1976d2' },
  { key: 'assets_edited', color: '#388e3c' },
  { key: 'annotations_validated', color: '#f57c00' },
];

const USER_COLORS = [
  '#7b1fa2',
  '#c2185b',
  '#0097a7',
  '#5d4037',
  '#455a64',
  '#e64a19',
  '#512da8',
  '#00796b',
];

export default {
  name: 'ProjectStatsGraph',
  components: { OverviewSectionCard, Autocomplete },
  mixins: [LoadUsersMixin],
  data() {
    return {
      stats: [],
      userStats: [],
      loading: false,
      selectedDays: 30,
      selectedUserIds: [],
      hiddenUserIds: [],
      hiddenSeriesKeys: [],
    };
  },
  computed: {
    projectId() {
      return sessionStorage.getItem('project_id');
    },
    daysOptions() {
      return [
        { title: this.$t('overview.days_7'), value: 7 },
        { title: this.$t('overview.days_30'), value: 30 },
        { title: this.$t('overview.days_90'), value: 90 },
        { title: this.$t('overview.days_180'), value: 180 },
      ];
    },
    usersById() {
      const map = {};
      (this.users || []).forEach((user) => {
        if (user && user.id != null) {
          map[user.id] = user;
        }
      });
      return map;
    },
    selectedUsers() {
      return this.selectedUserIds
        .map((id) => this.usersById[id] || this.userFromStats(id))
        .filter(Boolean);
    },
    availableUserOptions() {
      const selected = new Set(this.selectedUserIds);
      return (this.users || [])
        .filter((user) => user && !selected.has(user.id))
        .map((user) => ({
          ...user,
          name: this.userLabel(user),
        }));
    },
    chartRows() {
      const byDate = {};
      (this.stats || []).forEach((stat) => {
        if (!stat || !stat.date) {
          return;
        }
        byDate[stat.date] = {
          date: stat.date,
          items_added: Number(stat.items_added) || 0,
          assets_edited: Number(stat.assets_edited) || 0,
          annotations_validated: Number(stat.annotations_validated) || 0,
          user_saved: {},
        };
      });
      (this.userStats || []).forEach((userRow) => {
        const userId = userRow && userRow.user_id;
        if (userId == null) {
          return;
        }
        (userRow.stats || []).forEach((day) => {
          if (!day || !day.date) {
            return;
          }
          if (!byDate[day.date]) {
            byDate[day.date] = {
              date: day.date,
              items_added: 0,
              assets_edited: 0,
              annotations_validated: 0,
              user_saved: {},
            };
          }
          byDate[day.date].user_saved[userId] = Number(day.assets_saved) || 0;
        });
      });
      return Object.keys(byDate)
        .sort()
        .map((date) => byDate[date]);
    },
    displayedStats() {
      if (this.chartRows.length <= 30) {
        return this.chartRows;
      }

      const indicesToInclude = new Set();
      indicesToInclude.add(0);
      indicesToInclude.add(this.chartRows.length - 1);

      this.chartRows.forEach((stat, index) => {
        if (this.rowHasActivity(stat)) {
          indicesToInclude.add(index);
        }
      });

      const targetCount = 30;
      const remainingSlots = targetCount - indicesToInclude.size;
      if (remainingSlots > 0) {
        const availableIndices = [];
        for (let i = 0; i < this.chartRows.length; i++) {
          if (!indicesToInclude.has(i)) {
            availableIndices.push(i);
          }
        }
        if (availableIndices.length > 0) {
          const step = Math.ceil(availableIndices.length / remainingSlots);
          for (let i = 0; i < availableIndices.length; i += step) {
            indicesToInclude.add(availableIndices[i]);
            if (indicesToInclude.size >= targetCount) break;
          }
        }
      }

      const sortedIndices = Array.from(indicesToInclude).sort((a, b) => a - b);
      return sortedIndices.map((index) => this.chartRows[index]);
    },
    hiddenUserIdSet() {
      return new Set(this.hiddenUserIds);
    },
    hiddenSeriesKeySet() {
      return new Set(this.hiddenSeriesKeys);
    },
    maxValue() {
      if (this.displayedStats.length === 0) return 10;
      const allValues = [];
      this.displayedStats.forEach((stat) => {
        PROJECT_SERIES.forEach((series) => {
          if (!this.hiddenSeriesKeySet.has(series.key)) {
            allValues.push(stat[series.key] || 0);
          }
        });
        Object.entries(stat.user_saved || {}).forEach(([userId, value]) => {
          if (!this.hiddenUserIdSet.has(Number(userId))) {
            allValues.push(value);
          }
        });
      });
      const max = allValues.length ? Math.max(...allValues) : 0;
      return Math.ceil(max / 10) * 10 || 10;
    },
    chartWidth() {
      return 800;
    },
    chartHeight() {
      return 300;
    },
    chartPadding() {
      return 20;
    },
    projectSeries() {
      return PROJECT_SERIES.map((series) => ({
        ...series,
        visible: !this.hiddenSeriesKeySet.has(series.key),
        path: this.calculatePath(this.displayedStats.map((row) => row[series.key] || 0)),
      }));
    },
    visibleProjectSeries() {
      return this.projectSeries.filter((series) => series.visible);
    },
    userSeries() {
      return this.selectedUsers.map((user, index) => {
        const values = this.displayedStats.map(
          (row) => (row.user_saved && row.user_saved[user.id]) || 0,
        );
        const visible = !this.hiddenUserIdSet.has(user.id);
        return {
          id: user.id,
          label: this.userLabel(user),
          color: USER_COLORS[index % USER_COLORS.length],
          path: this.calculatePath(values),
          visible,
        };
      });
    },
    visibleUserSeries() {
      return this.userSeries.filter((series) => series.visible);
    },
    yAxisLabels() {
      const labels = [];
      const steps = 5;
      for (let i = steps; i >= 0; i--) {
        labels.push(Math.round((this.maxValue * i) / steps));
      }
      return labels;
    },
  },
  mounted() {
    this.selectedUserIds = getOverviewGraphUserIds(this.projectId);
    this.hiddenUserIds = getOverviewGraphHiddenUserIds(this.projectId);
    this.hiddenSeriesKeys = getOverviewGraphHiddenSeriesKeys(this.projectId);
    this.loadStats();
  },
  methods: {
    userLabel(user) {
      return editorDisplayName(user, user && (user.email || `User ${user.id}`));
    },
    userFromStats(userId) {
      const row = (this.userStats || []).find((item) => item.user_id === userId);
      if (!row) {
        return { id: userId, username: `User ${userId}` };
      }
      return {
        id: row.user_id,
        username: row.username,
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
      };
    },
    rowHasActivity(stat) {
      if (!stat) {
        return false;
      }
      if (stat.items_added > 0 || stat.assets_edited > 0 || stat.annotations_validated > 0) {
        return true;
      }
      return Object.values(stat.user_saved || {}).some((value) => value > 0);
    },
    async onAddUser(user) {
      if (!user || user.id == null) {
        return;
      }
      this.selectedUserIds = addOverviewGraphUserId(this.projectId, user.id);
      await this.loadUserStats();
    },
    async removeUser(userId) {
      this.selectedUserIds = removeOverviewGraphUserId(this.projectId, userId);
      this.hiddenUserIds = getOverviewGraphHiddenUserIds(this.projectId);
      await this.loadUserStats();
    },
    toggleUserVisibility(userId) {
      this.hiddenUserIds = toggleOverviewGraphUserVisibility(this.projectId, userId);
    },
    toggleSeriesVisibility(seriesKey) {
      this.hiddenSeriesKeys = toggleOverviewGraphSeriesVisibility(this.projectId, seriesKey);
    },
    async loadStats() {
      this.loading = true;
      try {
        const response = await this.$api.overview.get_project_stats(this.selectedDays);
        this.stats = response || [];
        await this.loadUserStats();
      } catch (error) {
        console.error('Error loading project stats:', error);
        this.stats = [];
        this.userStats = [];
      } finally {
        this.loading = false;
      }
    },
    async loadUserStats() {
      if (!this.selectedUserIds.length) {
        this.userStats = [];
        return;
      }
      try {
        const response = await this.$api.overview.get_user_stats(
          this.selectedDays,
          this.selectedUserIds,
        );
        this.userStats = response || [];
      } catch (error) {
        console.error('Error loading user stats:', error);
        this.userStats = [];
      }
    },
    calculatePath(values) {
      if (values.length === 0) return '';

      const width = this.chartWidth - (this.chartPadding * 2);
      const height = this.chartHeight - (this.chartPadding * 2);
      const maxVal = this.maxValue || 1;

      const points = values.map((value, index) => {
        const xRatio = values.length === 1 ? 0.5 : index / (values.length - 1);
        const x = this.chartPadding + xRatio * width;
        const y = this.chartPadding + height - (value / maxVal) * height;
        return `${x},${y}`;
      });

      return points.join(' ');
    },
  },
};
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.y-axis-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
  min-width: 40px;
  text-align: right;
  font-size: 12px;
  color: #666;
}

.y-axis-label {
  height: 0;
  line-height: 1;
}

.sparkline-container {
  position: relative;
  height: 300px;
  width: 100%;
  min-height: 300px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-svg {
  width: 100%;
  height: 100%;
  max-width: 100%;
}

.overview-stats-add-user {
  max-width: 360px;
  margin: 0 auto;
}

.overview-stats-user-chip-eye {
  cursor: pointer;
}

.overview-stats-user-chip-hidden,
.overview-stats-series-chip-hidden {
  opacity: 0.55;
}
</style>
